import { useEffect, useRef, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import "./PubGraph.css";
import _ from "lodash";
import { csv, json } from "d3-fetch";
import { select } from "d3-selection";
import { zoom } from "d3-zoom";
import { schemeCategory10 } from 'd3-scale-chromatic';
import { scaleOrdinal } from 'd3-scale';
import { color } from 'd3-color';
import { forceSimulation, forceCollide, forceLink, forceManyBody, forceX, forceY } from "d3-force";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo, faPlay, faStop } from '@fortawesome/free-solid-svg-icons';
import { peoplePromise } from './index.js';
import { groups } from './groups';
import { ProfileCard } from "./ProfileCard";
import { Range } from 'react-range';

export default function PubGraph(props) {

    const [graph, setGraph] = useState();
    const [graphData, setGraphData] = useState();
    const [filterOptions, setFilterOptions] = useState({ aggregation: "Személyenként", filterDept: "Mind", filterGroup: "Mind", minYear: 2011, maxYear: 2021, minCommon: 1, groupMember: true });
    const [forceOptions, setForceOptions] = useState({ nodeSize: "Normalizált relatív impakt faktor", linkWidth: "Közös publikációk száma" });
    const [displayOptions, setDisplayOptions] = useState({ drawLabels: true, nodeColor: "Tanszék szerint" });
    const [ profileCard, setProfileCard] = useState({visible: false, left: 0, right: 0, person: null, researchInfo: null});
    const simState = useRef({simulation: null, svgLink: null, svgNode: null, colors: null, depts: [], groups: []});
    const { width, height, ref } = useResizeDetector();

    function redrawGraph(graph) {
        if (graph) {
            const svg = select("#pub-graph-svg");
            const g = select("#pub-graph-svg-main");
            g.selectAll("*").remove();    
            const zoom_handler = zoom().on("zoom", (event) => g.attr("transform", event.transform));
            zoom_handler(svg);

            simState.current.svgLink = g.append("g")
                .attr("class", "pubgraph-links")
                .selectAll("line")
                .data(graph.links)
                .enter()
                .append("line")
                .attr("stroke-width", PubGraphOptions.linkWidths[forceOptions.linkWidth]);
        
            simState.current.svgNode = g.append("g")
                .attr("class", "pubgraph-nodes")
                .selectAll("g")
                .data(graph.nodes)
                .enter()
                .append("g");

            simState.current.svgNode.append("circle")
                .attr("r", PubGraphOptions.nodeSizes[forceOptions.nodeSize])
                .on("click", (evt,d) => setProfileCard({person: window.people[d.staffIndex], researchInfo: d, left: evt.pageX, right: evt.pageY, visible: true}));

            const baseColors = schemeCategory10;
            const colors = baseColors.concat(
                baseColors.map(d => color(d).darker(0.5).formatHex()),
                baseColors.map(d => color(d).brighter(0.5).formatHex()),
                baseColors.map(d => color(d).darker(1.0).formatHex()), 
                baseColors.map(d => color(d).brighter(1.0).formatHex()));
            
            simState.current.colors = scaleOrdinal([...Array(50).keys()], colors);
            decorate();
        }
        else {
            simState.current.svgNode = null;
            simState.current.svgLink = null;
        } 
    }

    useEffect(() => {
        if (!graphData) {
            Promise.all([peoplePromise(), csv("./graph/node_person.csv"), csv("./graph/relations_person.csv")].concat(groups.map(d => json("./data/"+d.file.hu))))
                .then(([ people, nodes, links, ...groups ]) => {
                    const tszCol = people.map(d => d["Tanszék"]);
                    simState.current.depts = tszCol.filter((d, ix) => tszCol.indexOf(d) === ix);                    
                    simState.current.groups = groups.map(d => d.name.hu);
                    nodes.forEach(d => d.staffIndex = people.findIndex(e => e.MTMT === d.MTMT));
                    nodes.forEach(d => d.groups = groups.filter(g => g.staff.find(s => s.mtmt === d.MTMT)).map(g => g.name.hu));
                    setGraphData({nodeData: nodes, linkData: links})
                });
        }
    }, [graphData]);

    function decorate() {        
        if (simState.current.svgNode) {
            simState.current.svgNode
                .selectAll("text")
                .remove();
            if (displayOptions.drawLabels === true)
                simState.current.svgNode
                    .append("text")
                    .text(d => d.staffIndex >= 0 ? window.people[d.staffIndex]["Név"] : d.MTMT)
                    .attr('x', 6)
                    .attr('y', 3)
            if (displayOptions.nodeColor === "Tanszék szerint")
                simState.current.svgNode
                    .selectAll("circle")
                    .attr("fill", d => simState.current.colors(d.staffIndex >= 0 ? simState.current.depts.indexOf(window.people[d.staffIndex]["Tanszék"]) : -1))
            else {
                simState.current.svgNode
                    .selectAll("circle")
                    .attr("fill", d => simState.current.colors(d.groups.length === 0 ? 49 : d.groups[0]))
            }
        }
    }

    function createGraph() {
        if (graphData) {
            const newNodes = graphData.nodeData.filter(node => 
                node.lastPub >= filterOptions.minYear 
                && node.firstPub <= filterOptions.maxYear
                && (!filterOptions.groupMember || node.groups.length > 0)
                && (filterOptions.filterDept === "Mind" || window.people[node.staffIndex]["Tanszék"] === filterOptions.filterDept)
                && (filterOptions.filterGroup === "Mind" || node.groups.includes(filterOptions.filterGroup))
            );
            const newLinks = graphData.linkData.filter(link => 
                newNodes.some(node => node.MTMT===link.source) 
                && newNodes.some(node => node.MTMT===link.target) 
                && link.pubCount >= filterOptions.minCommon
                //&& link.lastPub >= filterOptions.minYear 
                //&& link.firstPub <= filterOptions.maxYear
            );          
            console.log(`szűrés vége. ${newNodes.length} csomópont, ${newLinks.length} él`);

            //if (filterOptions.aggregation === "Személyenként")
                setGraph({nodes: _.cloneDeep(newNodes), links: _.cloneDeep(newLinks)});
            //else if (filterOptions.aggregation === "Kutatócsoportonként") {
                // TODO
            //}
        }
    }

    useEffect(createGraph, [graphData, filterOptions]);

    useEffect(() => {

        function simulationTicked() {
            if (simState.current.svgLink && simState.current.svgNode) {
                simState.current.svgLink
                    .attr("x1", d => d.source.x)
                    .attr("y1", d => d.source.y)
                    .attr("x2", d => d.target.x)
                    .attr("y2", d => d.target.y);    
                simState.current.svgNode
                    .attr("transform", d => "translate(" + d.x + "," + d.y + ")");
            }
        }

        if (simState.current.simulation)
            simState.current.simulation.stop();

        const newSimulation = forceSimulation()
            .force("link", forceLink().id(d => d.MTMT))
            .force("charge", forceManyBody())
            .force("x", forceX(width / 2).strength(0.05))
            .force("y", forceY(height / 2).strength(0.05))
            .force("collision", forceCollide());

        if (graph) {
            newSimulation
                .nodes(graph.nodes)
                .on("tick", () => simulationTicked());
            newSimulation
                .force("link")
                .links(graph.links);
            newSimulation
                .force("link")
                .strength(d => Math.max(0.1, Math.min(PubGraphOptions.linkWidths[forceOptions.linkWidth](d)/3, 1.5)));
            newSimulation
                .force("charge")
                .strength(-300);
            newSimulation
                .force("collision")
                .radius(PubGraphOptions.nodeSizes[forceOptions.nodeSize]);            
            newSimulation.alphaDecay(0.001);
            redrawGraph(graph);
        }
        simState.current.simulation = newSimulation;
    }, [graph, forceOptions]);

    useEffect(decorate, [displayOptions]);

    return <div className="pubgraph-main-container">
        {props.lang==="hu" && <p>Nagyítás: görgetéssel, vagy dupla klikk; kicsinyítés: görgetéssel, vagy shift + dupla klikk; mozgatás: nyomva tartott egérrel; információ: kattintás a csomópontra</p>}
        {props.lang!=="hu" && <p>Zooming in: mouse wheel or double click; zooming out: mouse wheel or shift + double click; panning: moving mouse with button pressed; information: click on the node</p>}
        <div className="pubgraph-main-center">
            <PubGraphSettings 
                lang={props.lang}
                departments={simState.current.depts}
                groups={simState.current.groups}
                displayOptions={displayOptions} 
                onDisplayOptionChange={opt => setDisplayOptions(opt)}
                filterOptions={filterOptions}
                onFilterOptionChange={opt => setFilterOptions(opt)}
                forceOptions={forceOptions}
                onForceOptionChange={opt => setForceOptions(opt)}
                onRestartSimulation={createGraph}
                onResumeSimulation={() => simState.current.simulation && simState.current.simulation.alpha(1).restart()}
                onStopSimulation={() => simState.current.simulation && simState.current.simulation.stop()}
            />
            <div className='pubgraph-area-panel' ref={ref}>                
                <svg id="pub-graph-svg" width={width} height={height}><g id="pub-graph-svg-main"></g></svg>
            </div>
        </div>
        {profileCard.visible && profileCard.person && <>
                <div className="background" onClick={() => setProfileCard({visible: false})}/>
                <ProfileCard lang={props.lang} 
                    person={profileCard.person} 
                    left={profileCard.left} 
                    right={profileCard.right} 
                    researchInfo={profileCard.researchInfo} 
                    onClick={() => setProfileCard({visible: false, person: null})}/>
            </> }
    </div>;
}

const PubGraphOptions = {

    nodeSizes: {
        "Fix": d => 5,
        "Publikációk száma": d => Math.max(4, 0.75*Math.sqrt(d.pubCount)),
        "Q szám": d => Math.max(4, 2*Math.sqrt(d.qScore)),
        "Normalizált Q szám": d => Math.max(4, 2.25*Math.sqrt(d.qnScore)),
        "Impakt faktor": d => Math.max(4, 1.5*Math.sqrt(d.ifScore)),
        "Normalizált impakt faktor": d => Math.max(4, 2.25*Math.sqrt(d.ifNormScore)),
        "Relatív impakt faktor": d => Math.max(4, 2.75*Math.sqrt(d.relifScore)),
        "Normalizált relatív impakt faktor": d => Math.max(4, 3.35*Math.sqrt(d.relifNormScore)),
        "Idézettség": d => Math.max(4, 0.35*Math.sqrt(d.citations)),
        "H index": d => Math.max(4, 0.65*d.hIndex)
    },
    linkWidths: {
        "Fix": d => 2,
        "Közös publikációk száma": d => Math.sqrt(d.pubCount)
    }
};

function PubGraphSettings(props) {
    const pf = props.filterOptions;
    return <div className="pubgraph-settings-panel">

        <div className="pubgraph-settings-row">
            <button 
                onClick={() => props.onRestartSimulation()} 
                className="pubgraph-settings-row-item"
            >
                <FontAwesomeIcon icon={faRedo} size="sm"/>
            </button>
            <button 
                onClick={() => props.onStopSimulation()} 
                className="pubgraph-settings-row-item"
            >
                <FontAwesomeIcon icon={faStop} size="sm"/>
            </button>
            <button 
                onClick={() => props.onResumeSimulation()} 
                className="pubgraph-settings-row-item"
            >
                <FontAwesomeIcon icon={faPlay} size="sm"/>
            </button>
        </div>

        {props.lang==="hu" && <h3>Szűrés</h3>}
        {props.lang!=="hu" && <h3>Filtering</h3>}

        <div className="pubgraph-settings-block">
            {props.lang==="hu" && <span className="pubgraph-settings-block-item">Aktivitás éve szerint:</span>}
            {props.lang!=="hu" && <span className="pubgraph-settings-block-item">By year of activity:</span>}
            <div className='pubgraph-settings-block-item range-container'>
                <Range step={1} min={1990} max={2022}
                    values={[props.filterOptions.minYear, props.filterOptions.maxYear]}
                    onChange={values => props.onFilterOptionChange({...props.filterOptions, minYear: values[0], maxYear: values[1]})}
                    renderTrack={({ props, children }) => (
                        <div
                          className='range'
                          onMouseDown={props.onMouseDown}
                          onTouchStart={props.onTouchStart}
                        >
                          <div
                            className='range-track'
                            ref={props.ref}
                          >
                            {children}
                          </div>
                        </div>
                      )}
                      renderThumb={({ index, props, isDragged }) => (
                        <div
                            className='range-thumb'
                            {...props}
                          style={{
                            ...props.style,
                          }}
                        >
                          <div className='range-label-back'>
                            {[pf.minYear, pf.maxYear][index]}
                          </div>
                        </div>
                      )}
                />
            </div>
        </div>

        <div className="pubgraph-settings-block">
            {props.lang==="hu" && <span className="pubgraph-settings-block-item">Tanszék szerint:</span>}
            {props.lang!=="hu" && <span className="pubgraph-settings-block-item">By department:</span>}
            <select className="pubgraph-settings-block-item"
                value={props.filterOptions.filterDept}
                onChange={event => props.onFilterOptionChange({...props.filterOptions, filterDept: event.currentTarget.value})}
            >
                {["Mind"].concat(props.departments).map((d, ix) => <option key={ix} value={d}>{d}</option>)}
            </select>
        </div>

        <div className="pubgraph-settings-block">
            {props.lang==="hu" && <span className="pubgraph-settings-block-item">Kutatócsoport szerint:</span>}
            {props.lang!=="hu" && <span className="pubgraph-settings-block-item">By research group:</span>}
            <select 
                className="pubgraph-settings-block-item"
                value={props.filterOptions.filterGroup}
                onChange={event => props.onFilterOptionChange({...props.filterOptions, filterGroup: event.currentTarget.value})}
            >              
                {["Mind"].concat(props.groups).map((d, ix) => <option key={ix} value={d}>{d}</option>)}
            </select>
        </div>

        <div className="pubgraph-settings-block">
            {props.lang==="hu" && <span className="pubgraph-settings-block-item">Közös cikkek min. száma:</span>}
            {props.lang!=="hu" && <span className="pubgraph-settings-block-item">Min. number of common papers:</span>}
            <input className='pubgraph-settings-block-item'
                type="number"
                min={1} 
                max={1000} 
                value={props.filterOptions.minCommon}
                onChange={event => props.onFilterOptionChange({...props.filterOptions, minCommon: +event.target.value})}
            />
        </div>          

        <div className="pubgraph-settings-block">
            <div className='pubgraph-settings-row'>
                <input 
                    type="checkbox"
                    checked={props.filterOptions.groupMember}
                    label="onlyMembers"
                    onChange={evt => props.onFilterOptionChange({...props.filterOptions, groupMember: evt.target.checked})}
                />
                    {props.lang==="hu" && <label htmlFor="onlyMembers">Csak kutatócsoport tagok</label>}
                    {props.lang!=="hu" && <label htmlFor="onlyMembers">Research group members only</label>}
            </div>
        </div>

        {props.lang==="hu" && <h3>Elrendezés</h3>}
        {props.lang!=="hu" && <h3>Layout</h3>}

        <div className="pubgraph-settings-block">
            {props.lang==="hu" && <span className="pubgraph-settings-block-item">Csomópontméret:</span>}
            {props.lang!=="hu" && <span className="pubgraph-settings-block-item">Node size:</span>}
            <select 
                className="pubgraph-settings-block-item"
                value={props.forceOptions.nodeSize}
                onChange={event => props.onForceOptionChange({...props.forceOptions, nodeSize: event.currentTarget.value})}
            >              
                {Object.keys(PubGraphOptions.nodeSizes).map((d, ix) => <option key={ix} value={d}>{d}</option>)}
            </select>
        </div>

        <div className="pubgraph-settings-block">
            {props.lang==="hu" && <span className="pubgraph-settings-block-item">Élvastagság:</span>}
            {props.lang!=="hu" && <span className="pubgraph-settings-block-item">Edge width:</span>}            
            <select 
                className="pubgraph-settings-block-item"
                value={props.forceOptions.linkWidth}
                onChange={event => props.onForceOptionChange({...props.forceOptions, linkWidth: event.currentTarget.value})}
            >              
                {Object.keys(PubGraphOptions.linkWidths).map((d, ix) => <option key={ix} value={d}>{d}</option>)}
            </select>
        </div>

        {props.lang==="hu" && <h3>Megjelenítés</h3>}
        {props.lang!=="hu" && <h3>Display</h3>}

        {/*<div className="pubgraph-settings-block">
            <span className="pubgraph-settings-block-item">Aggregáció:</span>
            <select 
                className="pubgraph-settings-block-item"
                value={props.filterOptions.aggregation}
                onChange={event => props.onFilterOptionChange({...props.filterOptions, aggregation: event.currentTarget.value})}
            >              
                {["Személyenként", "Kutatócsoportonként"].map((d, ix) => <option key={ix} value={d}>{d}</option>)}
            </select>
        </div>*/}

        <div className="pubgraph-settings-block">
            {props.lang==="hu" && <span className="pubgraph-settings-block-item">Csomópontok színe:</span>}
            {props.lang!=="hu" && <span className="pubgraph-settings-block-item">Node colors:</span>}
            <select 
                className="pubgraph-settings-block-item"
                value={props.displayOptions.nodeColor}
                onChange={event => props.onDisplayOptionChange({...props.displayOptions, nodeColor: event.currentTarget.value})}
            >              
                {["Tanszék szerint", "Kutatócsoport szerint"].map((d, ix) => <option key={ix} value={d}>{d}</option>)}
            </select>
        </div>

        <div className='pubgraph-settings-row'>
            <input 
                type="checkbox"
                checked={props.displayOptions.drawLabels}
                label="drawLabels"
                onChange={evt => props.onDisplayOptionChange({...props.displayOptions, drawLabels: evt.target.checked})}
            />
                {props.lang==="hu" && <label htmlFor="drawLabels">Cimkék megjelenítése</label>}
                {props.lang!=="hu" && <label htmlFor="drawLabels">Show labels</label>}
        </div>

    </div>;
}
